import { useEffect, useState } from 'react';
import { ChevronRight, FileDigit, NotebookText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';

import TestWiseMistakes from './TestWiseMistakes/TestWiseMistakes';
import ChapterWiseMistakes from './ChapterWiseMistakes/ChapterWiseMistakes';
import { AppLayout } from '../Layout/AppLayout';
import { usePackStorage } from '../Layout/usePackStorage';
import { useSearchParams } from 'react-router-dom';

const categories = [
  {
    id: 'test-wise',
    title: 'Test-wise',
    icon: NotebookText,
    hasNewBadge: false,
  },
  {
    id: 'chapter-wise',
    title: 'Chapter-wise',
    icon: FileDigit,
    hasNewBadge: false,
  },
];

export default function MyMistakes() {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const [currentView, setCurrentView] = useState(type || 'test-wise');
  const { purchasedPacks, selectedPack, handlePackChange } = usePackStorage();
  const user = JSON.parse(localStorage.getItem('userProfile'));

  const [isTestWiseDetailed, setIsTestWiseDetailed] = useState(false);
  const [isChapterWiseDetailed, setIsChapterWiseDetailed] = useState(false);
  const handleTestWiseViewChange = (isDetailed) => {
    setIsTestWiseDetailed(isDetailed);
  };

  const handleChapterWiseViewChange = (isDetailed) => {
    setIsChapterWiseDetailed(isDetailed);
  };

  const isDetailedView = isTestWiseDetailed || isChapterWiseDetailed;

  return (
    <AppLayout purchasedPacks={purchasedPacks} selectedPack={selectedPack} onPackChange={handlePackChange}>
      <div className="h-full w-full max-w-7xl">
        <div className="w-full px-4 py-6 md:py-8">
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 w-full">
            {/* Mobile Navigation - Conditionally RENDER if not in detailed view */}
            {!isDetailedView && (
              <div className="lg:hidden w-full">
                <h1 className="text-lg font-bold text-[var(--q3-neutral-default)] mb-4">My Mistakes</h1>
                <div className="overflow-x-auto">
                  <div className="flex space-x-2 pb-2 min-w-max">
                    {categories.map((category) => (
                      <Button
                        key={category.id}
                        variant={currentView === category.id ? 'default' : 'outline'}
                        size="sm"
                        className={cn(
                          'flex-shrink-0 whitespace-nowrap',
                          currentView === category.id
                            ? 'bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)]'
                            : 'text-[var(--q3-neutral-default)] border-[var(--q3-stroke-normal)]',
                        )}
                        onClick={() => setCurrentView(category.id)}
                      >
                        <category.icon className="h-4 w-4 mr-2" />
                        {category.title}
                        {category.hasNewBadge && (
                          <Badge className="bg-[var(--q3-danger-normal)] text-[var(--q3-neutral-default_inverted)] text-xs px-1 py-0 ml-2">
                            NEW
                          </Badge>
                        )}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {!isDetailedView && (
              <div className="hidden lg:block w-64 flex-shrink-0">
                <h2 className="text-2xl font-bold mb-7 text-[var(--q3-neutral-default)]">My Mistakes</h2>
                <div className="space-y-5">
                  {categories.map((category) => (
                    <Button
                      key={category.id}
                      variant={currentView === category.id ? 'default' : 'ghost'}
                      className={cn(
                        'w-full group py-6 rounded-xl justify-start text-left font-normal',
                        currentView === category.id
                          ? 'bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)] hover:bg-[var(--q3-neutral-default)]/90'
                          : 'text-[var(--q3-neutral-default)] hover:bg-[var(--q3-surface-dim)]',
                      )}
                      onClick={() => setCurrentView(category.id)}
                    >
                      <div className="flex items-center justify-between w-full gap-4">
                        <div className="flex items-center gap-2 min-w-0">
                          <category.icon className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{category.title}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {category.hasNewBadge && (
                            <Badge className="bg-[var(--q3-danger-normal)] rounded-full text-[var(--q3-neutral-default_inverted)] text-xs px-2 py-0.5 flex-shrink-0">
                              NEW
                            </Badge>
                          )}
                          <ChevronRight className="h-4 w-4 flex-shrink-0 transform transition-transform duration-200 group-hover:translate-x-1" />
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex-1 min-w-0 w-full overflow-hidden">
              <div className="w-full h-full">
                {currentView === 'test-wise' && <TestWiseMistakes onViewChange={handleTestWiseViewChange} />}
                {currentView === 'chapter-wise' && <ChapterWiseMistakes onViewChange={handleChapterWiseViewChange} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
